/**
 * Confirmed Atrias end-of-contract outcome (FT-4389), normalized from the
 * `sdps_*.eocStatus` value that `/allservices` carries on each injection /
 * offtake row — the DURABLE terminal state.
 *
 * `eocStatus` is an Atrias CODE MIRROR column, not a label column: it stores
 * the raw Atrias `StatusType` as a string — '39' Approved, '41' Rejected, '40'
 * Withdrawn, '64' Cancelled, '97' Pending, plus alphanumerics such as 'BL7'.
 * The LABEL vocabulary lives on a different column entirely:
 * `mig6_eoc_requests.status` (`pending` / `approved` / `rejected`, enum
 * constrained), exposed by the SEPARATE endpoint GET
 * /sdp/end-of-contract/:companyId — the transient in-flight 'pending' source.
 * The two are read independently and are never cross-compared.
 *
 * A mig6-ws parser regression also wrote the label words into `eocStatus`, so
 * live rows hold BOTH vocabularies. Both are therefore accepted here: that is
 * what makes the parser / backend / frontend rollout order-independent and
 * non-breaking, so neither vocabulary may be dropped.
 *
 * Only '39' / 'approved' means approved, and only '41' / 'rejected' means
 * rejected. EVERY other value — the remaining Atrias codes ('40', '64', '97',
 * 'BL7', …), the empty string, NULL and anything unrecognised — maps to `null`:
 * no outcome, no badge, and no selection lock.
 */
export type EocOutcome = "approved" | "rejected" | null;

/**
 * Known values observed on `eocStatus`. Documentation only — the column can
 * hold any Atrias StatusType, so consumers type the field as `string | null`
 * and rely on `normalizeEocStatus` to gate it.
 */
export const EOC_STATUS_APPROVED_CODE = "39";
export const EOC_STATUS_REJECTED_CODE = "41";

/**
 * The single gate between the raw `eocStatus` wire value and any FE behavior
 * (outcome badges AND the non-endable selection lock in EndContractManager).
 * Trim + case-insensitive, so ' 39 ' and 'APPROVED' both resolve.
 */
export const normalizeEocStatus = (raw: string | null | undefined): EocOutcome => {
  const s = (raw ?? "").trim().toLowerCase();
  if (s === EOC_STATUS_APPROVED_CODE || s === "approved") return "approved";
  if (s === EOC_STATUS_REJECTED_CODE || s === "rejected") return "rejected";
  return null;
};

/**
 * `eocStatusReason` value that marks a CUSTOMER SWITCH-AWAY, not an
 * end-of-contract. Three independent writers stamp `eocStatus = '39'` on an
 * sdps row, and only one of them is a genuine EOC:
 *
 * - reason 'COMPLETED' → the SDP was handed to another supplier by a Customer
 *   Switch. Written by yuso-portals-backend
 *   (src/functions/customerSwitch.ts — `SET eocDate = ?, eocStatus = '39',
 *   eocStatusReason = 'COMPLETED', withYuso = 'N'`) and by the legacy
 *   my-yuso server (server/src/api/sdps.js, the same triplet). The customer
 *   never requested an end-of-contract, so EOC-acceptance wording would be a
 *   false statement about their own action.
 * - reason NULL → a genuine EOC approval, written by the yuso-mig6-ws Atrias
 *   parser off the real BS3 response.
 *
 * `eocRequestDate` is NOT usable as the discriminator: no repo writes that
 * column, so it is NULL for genuine EOCs too.
 *
 * Compared trim + case-insensitive like `normalizeEocStatus`: this is a
 * free-text varchar written by three independent writers across two repos, so
 * tolerating case/whitespace drift costs nothing.
 */
export const EOC_STATUS_REASON_CUSTOMER_SWITCH = "completed";

/** Whether an `eocStatusReason` marks a customer switch-away (see above). */
export const isSwitchAwayReason = (raw: string | null | undefined): boolean =>
  (raw ?? "").trim().toLowerCase() === EOC_STATUS_REASON_CUSTOMER_SWITCH;

/**
 * The outcome to use for BADGES / WORDING ONLY — `normalizeEocStatus` with the
 * customer switch-away suppressed. Layered strictly ON TOP of the normalizer,
 * which stays the one gate for the SELECTION LOCK.
 *
 * NEVER use this for endability. A switched-away SDP is every bit as terminal
 * as an EOC-ended one: its contract is closed and it must stay non-endable, so
 * the lock keeps reading `normalizeEocStatus`. Hiding the badge must not make
 * the row re-submittable — that would be a worse bug than the wrong wording.
 *
 * Scope: only the APPROVED outcome is suppressed. 'COMPLETED' is written only
 * ever alongside '39' (verified in both writers), and a 'Rejected' badge makes
 * no claim that an ending was accepted, so a rejection is shown whatever the
 * reason says — that keeps the genuine rejection reason visible.
 */
export const eocBadgeOutcome = (
  rawStatus: string | null | undefined,
  rawReason: string | null | undefined
): EocOutcome => {
  const outcome = normalizeEocStatus(rawStatus);
  if (outcome === "approved" && isSwitchAwayReason(rawReason)) return null;
  return outcome;
};
